// The published-run realm is implemented separately from the canvas scheduler.
// This adapter is the only teardown seam the scheduler knows about: the realm
// controller can install its terminator and report which sibling run ids died
// when one timeout tears the shared disposable realm down.

let _adapter = null;
const _listeners = new Set();

export function setKernelRunRealmAdapter(adapter) {
    const previous = _adapter;
    _adapter = adapter && typeof adapter === "object" ? adapter : null;
    return () => {
        if (_adapter === adapter) _adapter = previous;
    };
}

export function subscribeKernelRunRealmTeardown(listener) {
    if (typeof listener !== "function") return () => {};
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

export function reportKernelRunRealmTeardown(event) {
    if (!event || typeof event !== "object") return;
    const normalized = {
        ...event,
        cancelledRunIds: Array.isArray(event.cancelledRunIds)
            ? [...new Set(event.cancelledRunIds.filter((id) => typeof id === "string" && id))]
            : [],
    };
    for (const listener of [..._listeners]) {
        try { listener(normalized); } catch { /* a status listener cannot block teardown */ }
    }
}

function normalizeTermination(raw, context) {
    const result = raw && typeof raw === "object" ? raw : { terminated: raw !== false };
    const cancelledRunIds = Array.isArray(result.cancelledRunIds)
        ? result.cancelledRunIds
        : Array.isArray(result.collateralRunIds)
            ? result.collateralRunIds
            : [];
    const normalized = { ...result, cancelledRunIds };
    if (cancelledRunIds.length || result.all === true) {
        reportKernelRunRealmTeardown({
            ...normalized,
            triggerRunId: context?.runId || null,
            executionId: context?.executionId || null,
            triggerReason: context?.reason || null,
        });
    }
    return normalized;
}

export function terminateKernelRunRealm(context) {
    const terminate = _adapter?.terminateRunRealm || _adapter?.terminate;
    if (typeof terminate !== "function") {
        return { terminated: false, cancelledRunIds: [] };
    }
    try {
        const raw = terminate.call(_adapter, context);
        if (raw && typeof raw.then === "function") {
            return Promise.resolve(raw).then(
                (result) => normalizeTermination(result, context),
                (error) => ({ terminated: false, cancelledRunIds: [], error }),
            );
        }
        return normalizeTermination(raw, context);
    } catch (error) {
        return { terminated: false, cancelledRunIds: [], error };
    }
}

export function releaseKernelRunRealm(context) {
    const release = _adapter?.releaseRunRealm || _adapter?.release;
    if (typeof release !== "function") return { released: false };
    try {
        return release.call(_adapter, context);
    } catch (error) {
        return { released: false, error };
    }
}

export function _resetKernelRunRealmAdapterForTests() {
    _adapter = null;
    _listeners.clear();
}
